import { Anchor, Button, Input, Modal, Toast } from '@byecode/ui'
import { ErrorMessage, TagIcon, useAtomAction, useAtomData } from '@lighthouse/shared'
import { find } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useAsyncFn } from 'react-use'

import { fetchOtherPlatformsAtom } from '@/atoms/application/action'
import { otherPlatformListAtom } from '@/atoms/application/state'
import {
    otherPlatformBackgroundColorMap,
    otherPlatformColorMap,
    otherPlatformDescribeMap,
    otherPlatformNameMap
} from '@/containers/OtherPlatforms/constant'
import * as srv from '@/services'

import { PLACEHOLDER_PASSWORD } from '../constants'
import * as SC from '../styles'
import type { WeComAccountFormState } from './type'

interface WeComAccountProps {
    onClose?: () => void
}

const initData: WeComAccountFormState = {
    senderName: '',
    emailAddress: '',
    password: ''
}
const WeComEmailAccount: React.FunctionComponent<WeComAccountProps> = ({ onClose }) => {
    const otherPlatforms = useAtomData(otherPlatformListAtom)
    const { run: fetchOtherPlatforms } = useAtomAction(fetchOtherPlatformsAtom)

    const [hide, setHide] = useState(true)

    const account = useMemo(() => {
        const otherPlatform = find(item => item.type === 'WE_COM_MAIL', otherPlatforms ?? [])
        return otherPlatform?.configurationComplete && otherPlatform.type === 'WE_COM_MAIL'
            ? { ...otherPlatform?.config, password: PLACEHOLDER_PASSWORD, id: otherPlatform.id }
            : { ...initData, id: otherPlatform?.id }
    }, [otherPlatforms])

    const {
        control,
        register,
        handleSubmit,
        formState: { errors }
    } = useForm({
        mode: 'onSubmit',
        defaultValues: account
    })

    const [{ loading: submitLoading }, doUpdateIntegration] = useAsyncFn(srv.updateIntegration)
    const handleUpdate = useCallback(
        async (params: WeComAccountFormState) => {
            const { id, senderName, emailAddress, password } = params
            if (!id || submitLoading) {
                return
            }
            const res = await doUpdateIntegration({
                id,
                type: 'WE_COM_MAIL',
                config: { password: password === PLACEHOLDER_PASSWORD ? undefined : password, senderName, emailAddress }
            })
            if (res) {
                fetchOtherPlatforms()
                onClose?.()
            }
        },
        [onClose, fetchOtherPlatforms, submitLoading, doUpdateIntegration]
    )

    const handleInviteSubmit = useCallback(
        (ev: React.FormEvent) => {
            ev.preventDefault()
            handleSubmit(handleUpdate)()
        },
        [handleSubmit, handleUpdate]
    )

    return (
        <SC.Container onSubmit={handleInviteSubmit}>
            <SC.Content>
                <TagIcon
                    size={48}
                    iconSize={32}
                    icon="BrandLogoWeCom"
                    radius={6}
                    iconColor={otherPlatformColorMap['WE_COM_MAIL']}
                    background={otherPlatformBackgroundColorMap['WE_COM_MAIL']}
                />
                <SC.Title> {otherPlatformNameMap['WE_COM_MAIL']}</SC.Title>
                <SC.Item>
                    <SC.Description> {otherPlatformDescribeMap['WE_COM_MAIL']}</SC.Description>
                </SC.Item>
                <SC.Tip>
                    <SC.Icon color="var(--color-main)" type="WarningCircle" />
                    <SC.Text>如果您完善授权时遇到问题，请查看帮助文档：</SC.Text>
                    <Anchor
                        target="_blank"
                        style={{ fontWeight: 600, color: 'var(--color-main)' }}
                        to="https://byecodehelp.yuque.com/org-wiki-byecodehelp-zavfcl/oevich/eta8qc1o4c30gm9s?singleDoc#"
                    >
                        企业腾讯邮箱授权配置
                    </Anchor>
                </SC.Tip>

                <SC.Item>
                    <SC.Text>
                        发件人名称
                        <SC.Text color="red" style={{ paddingLeft: 4 }}>
                            *
                        </SC.Text>
                    </SC.Text>
                    <ErrorMessage name="senderName" errors={errors}>
                        <Input {...register('senderName', { required: '不能为空' })} />
                    </ErrorMessage>

                    <SC.Description>收件人看到的名称，请检查核对</SC.Description>
                </SC.Item>
                <SC.Item>
                    <SC.Text>
                        发件人邮箱地址
                        <SC.Text color="red" style={{ paddingLeft: 4 }}>
                            *
                        </SC.Text>
                    </SC.Text>
                    <ErrorMessage name="emailAddress" errors={errors}>
                        <Input autoComplete="new-password" {...register('emailAddress', { required: '不能为空' })} />
                    </ErrorMessage>
                    <SC.Description>您的腾讯企业邮箱地址，如 address@exmail.qq.com。请先在腾讯企业邮箱开启SMTP服务</SC.Description>
                </SC.Item>
                <SC.Item>
                    <SC.Text>
                        发件人邮箱密码
                        <SC.Text color="red" style={{ paddingLeft: 4 }}>
                            *
                        </SC.Text>
                    </SC.Text>
                    <ErrorMessage name="password" errors={errors}>
                        <Controller
                            name="password"
                            control={control}
                            rules={{ required: '不能为空' }}
                            render={({ field }) => {
                                return (
                                    <Input
                                        type={hide ? 'password' : 'text'}
                                        autoComplete="new-password"
                                        onFocus={() => {
                                            if (field.value === PLACEHOLDER_PASSWORD) {
                                                field.onChange('')
                                            }
                                        }}
                                        onBlur={() => {
                                            if (!field.value.trim()) {
                                                field.onChange(PLACEHOLDER_PASSWORD)
                                            }
                                        }}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                )
                            }}
                        />
                    </ErrorMessage>
                </SC.Item>
            </SC.Content>
            <SC.Footer>
                <SC.LeftFill />
                <SC.RightFill>
                    <Button size="lg" style={{ width: 104 }} onClick={() => onClose?.()}>
                        取消
                    </Button>
                    <Button size="lg" loading={submitLoading} style={{ width: 104 }} type="primary" htmlType="submit">
                        确定
                    </Button>
                </SC.RightFill>
            </SC.Footer>
        </SC.Container>
    )
}

export default WeComEmailAccount
